// A small layer beside what opened it (a menu, the day picker), kept on screen, closed by Esc or a press
// outside it.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "../../lib/util";

export function Pop({ at, onClose, className, children, width = 220, side = "below" }: { at: DOMRect; onClose: () => void; className?: string; children: ReactNode; width?: number; side?: "below" | "right" | "left" }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: at.left, top: at.bottom + 6 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const want = side === "right" ? { left: at.right + 8, top: at.top } : side === "left" ? { left: at.left - width - 8, top: at.top } : { left: at.left, top: at.bottom + 6 };
    const left = Math.max(10, Math.min(want.left, window.innerWidth - width - 10));
    // below, unless it would run off the foot: then above what opened it
    const top = side === "below" && want.top + h > window.innerHeight - 10 ? Math.max(10, at.top - h - 6) : Math.max(10, Math.min(want.top, window.innerHeight - h - 10));
    setPos({ left, top });
  }, [at, width, side]);
  useEffect(() => {
    const down = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && onClose();
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("keydown", key, true);
    };
  }, [onClose]);
  return createPortal(
    <div ref={ref} className={cx("menu island ccal-pop", className)} style={{ ...pos, width }} data-esc-own role="menu">
      {children}
    </div>,
    document.querySelector(".board-root") ?? document.body,
  );
}
